/**
 * Design Tokens for Mindrealm Dashboard
 *
 * Centralized design system with all visual constants.
 */

const fonts = {
  display: 'ui-serif, "Times New Roman", Georgia, serif',
  body: 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
} as const;

const backgrounds = {
  // Base page backgrounds (white/black)
  page: {
    light: '#fafafa',
    dark: '#05060f',
  },
  // Alternating section backgrounds (purple tints)
  section: {
    light: '#f8f5fb',
    dark: '#100d1a',
  },
  // Skeleton loading states
  skeleton: {
    light: 'rgba(0, 0, 0, 0.15)',
    dark: 'rgba(255, 255, 255, 0.1)',
  },
  // Navigation transparent variant
  navigation: {
    transparent: {
      light: 'rgba(255, 255, 255, 0.8)',
      dark: 'rgba(20, 18, 30, 0.8)',
    },
  },
} as const;

const badges = {
  success: {
    light: {
      bg: '#dbeafe',
      border: '#93c5fd',
      text: '#0369a1',
    },
    dark: {
      bg: '#1e3a8a',
      border: '#3b82f6',
      text: '#93c5fd',
    },
  },
  warning: {
    light: {
      bg: '#fefce8',
      border: '#d4c517',
      text: '#854d0e',
    },
    dark: {
      bg: '#713f12',
      border: 'rgba(254, 249, 195, 0.4)',
      text: '#fef9c3',
    },
  },
  error: {
    light: {
      bg: '#fce7f3',
      border: '#f9a8d4',
      text: '#9f1239',
    },
    dark: {
      bg: '#831843',
      border: '#ec4899',
      text: '#f9a8d4',
    },
  },
  info: {
    light: {
      bg: '#ede9fe',
      border: '#c4b5fd',
      text: '#6d28d9',
    },
    dark: {
      bg: '#4c1d95',
      border: '#a78bfa',
      text: '#c4b5fd',
    },
  },
} as const;

const buttons = {
  gradients: {
    primary: {
      light: 'linear-gradient(120deg, #16a34a, #14b8a6, #9333ea)',
      dark: 'linear-gradient(120deg, #22c55e, #2dd4bf, #a855f7)',
    },
    secondary: {
      light: 'linear-gradient(to bottom right, #f5d870, #c9a5e8, #7db5e8)',
      dark: 'linear-gradient(to bottom right, rgba(254, 249, 195, 0.75), rgba(196, 181, 253, 0.75), rgba(147, 197, 253, 0.75))',
    },
    disabled: {
      light: 'linear-gradient(to bottom right, #9ca3af, #d1d5db, #e5e7eb)',
      dark: 'linear-gradient(to bottom right, #4b5563, #6b7280, #9ca3af)',
    },
  },
  disabled: {
    light: {
      bg: 'rgba(0, 0, 0, 0.1)',
      text: 'rgba(0, 0, 0, 0.4)',
    },
    dark: {
      bg: 'rgba(255, 255, 255, 0.1)',
      text: 'rgba(255, 255, 255, 0.4)',
    },
  },
} as const;

const shadows = {
  button: {
    primary: {
      light: {
        default: '0 0 20px rgba(155, 94, 238, 0.2)',
        hover: '0 0 30px rgba(155, 94, 238, 0.3)',
      },
      dark: {
        default: '0 0 10px rgba(155, 94, 238, 0.3)',
        hover: '0 0 15px rgba(155, 94, 238, 0.4)',
      },
    },
    secondary: {
      light: {
        default: '0 1px 2px rgba(0, 0, 0, 0.1)',
        hover: '0 2px 4px rgba(0, 0, 0, 0.15)',
      },
      dark: {
        default: '0 1px 2px rgba(255, 255, 255, 0.1)',
        hover: '0 2px 4px rgba(255, 255, 255, 0.15)',
      },
    },
    disabled: {
      light: {
        default: 'none',
        hover: 'none',
      },
      dark: {
        default: 'none',
        hover: 'none',
      },
    },
  },
  text: {
    primary: '0 2px 4px rgba(0, 0, 0, 0.5)',
    secondary: '0 2px 4px rgba(0, 0, 0, 0.8)',
    badge: '0 1px 2px rgba(0, 0, 0, 0.3)',
  },
  glass: {
    card: {
      light: {
        default:
          '0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        hover:
          '0 8px 32px 0 rgba(31, 38, 135, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1), 0 0 24px rgba(196, 181, 253, 0.3)',
      },
      dark: {
        default:
          '0 8px 32px 0 rgba(0, 0, 0, 0.4), inset 0 1px 0 0 rgba(196, 181, 253, 0.05)',
      },
    },
  },
} as const;

const focusRings = {
  primary: {
    color: '#9b5eee',
    width: '2px',
    offset: '2px',
  },
  secondary: {
    color: '#c9a5e8',
    width: '2px',
    offset: '2px',
  },
} as const;

const textOpacity = {
  full: 1,
  high: 0.8,
  medium: 0.7,
  low: 0.6,
  disabled: 0.4,
} as const;

const borders = {
  default: 'rgba(0, 0, 0, 0.2)',
  focus: 'rgba(0, 0, 0, 0.4)',
  tabs: {
    bottom: 'rgba(196, 181, 253, 0.3)',
    active: '#c4b5fd',
  },
  footer: {
    light: 'rgba(0, 0, 0, 0.1)',
    dark: 'rgba(255, 255, 255, 0.1)',
  },
} as const;

const gradient = {
  hero: {
    light: 'linear-gradient(120deg, #16a34a, #14b8a6, #9333ea)',
    dark: 'linear-gradient(120deg, #22c55e, #2dd4bf, #a855f7)',
  },
} as const;

const glass = {
  card: {
    backdropBlur: 'blur(12px)',
    border: {
      light: 'rgba(0, 0, 0, 0.08)',
      dark: 'rgba(255, 255, 255, 0.08)',
    },
    bg: {
      light: 'rgba(0, 0, 0, 0.05)',
      dark: 'rgba(20, 18, 30, 0.4)',
    },
  },
} as const;

const box = {
  purple: {
    bg: {
      light: 'rgba(124, 58, 237, 0.06)',
      dark: 'rgba(139, 92, 246, 0.10)',
    },
    border: {
      light: 'rgba(124, 58, 237, 0.12)',
      dark: 'rgba(139, 92, 246, 0.18)',
    },
  },
  purpleNumber: {
    bg: {
      light: 'rgba(124, 58, 237, 0.15)',
      dark: 'rgba(139, 92, 246, 0.20)',
    },
    color: {
      light: '#7c3aed',
      dark: '#a78bfa',
    },
  },
  // Teal accent number circles (for principles, etc.)
  accentNumber: {
    bg: {
      light: 'rgba(16, 185, 129, 0.15)',
      dark: 'rgba(16, 185, 129, 0.20)',
    },
    color: {
      light: '#059669',
      dark: '#10b981',
    },
  },
  gradient: {
    bg: 'linear-gradient(135deg, rgba(22, 163, 74, 0.1), rgba(147, 51, 234, 0.1))',
  },
  // Outline card: transparent fill + teal-tinted border. Alternates with the
  // filled purple card across sections to break the repeating-box monotony.
  outline: {
    border: {
      light: 'rgba(5, 150, 105, 0.35)',
      dark: 'rgba(16, 185, 129, 0.32)',
    },
  },
} as const;

const spacing = {
  mobilePadding: '24px',
  desktopPadding: '64px',
  maxWidth: '1400px',
  contentMaxWidth: '1200px',
} as const;

const typography = {
  scale: {
    h1: {
      fontSize: '3.75rem',
      lineHeight: '1',
      letterSpacing: '-0.02em',
      fontWeight: '700',
    },
    h2: {
      fontSize: '3rem',
      lineHeight: '1.1',
      letterSpacing: '-0.02em',
      fontWeight: '700',
    },
    h3: {
      fontSize: '2.25rem',
      lineHeight: '1.2',
      letterSpacing: '-0.01em',
      fontWeight: '600',
    },
    h4: {
      fontSize: '1.875rem',
      lineHeight: '1.3',
      letterSpacing: '-0.01em',
      fontWeight: '600',
    },
    h5: {
      fontSize: '1.5rem',
      lineHeight: '1.4',
      letterSpacing: '0',
      fontWeight: '600',
    },
    h6: {
      fontSize: '1.25rem',
      lineHeight: '1.5',
      letterSpacing: '0',
      fontWeight: '600',
    },
    body: {
      fontSize: '1rem',
      lineHeight: '1.5',
      letterSpacing: '0',
      fontWeight: '400',
    },
    caption: {
      fontSize: '0.875rem',
      lineHeight: '1.4',
      letterSpacing: '0',
      fontWeight: '400',
    },
  },
} as const;

const motion = {
  transition: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },
  easing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

const colors = {
  semantic: {
    success: '#93c5fd',
    warning: '#fef9c3',
    error: '#f9a8d4',
    info: '#c4b5fd',
  },
  // Theme accent colors - change these to switch the entire theme's color scheme
  accent: {
    primary: {
      light: '#059669', // emerald-600 (teal)
      dark: '#10b981', // emerald-500 (teal)
    },
    secondary: {
      light: '#9333ea', // purple-600
      dark: '#a855f7', // purple-500
    },
  },
  text: {
    primary: {
      light: '#05060f', // Dark text on light backgrounds
      dark: '#fafafa', // Light text on dark backgrounds
    },
    secondary: {
      light: 'rgba(5, 6, 15, 0.7)', // Dimmed dark text on light backgrounds
      dark: 'rgba(250, 250, 250, 0.7)', // Dimmed light text on dark backgrounds
    },
    onGradient: '#ffffff',
  },
} as const;

export const theme = {
  fonts,
  backgrounds,
  badges,
  buttons,
  shadows,
  focusRings,
  textOpacity,
  borders,
  gradient,
  glass,
  box,
  spacing,
  typography,
  motion,
  colors,
} as const;
